import React from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  List,
  ListItemButton,
  Stack,
  CircularProgress,
  Button,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Done as DoneIcon,
  Reply as ReplyIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { formatRelativeTime } from '../../utils/dashboardUtils';
import {
  useSupportConversation,
  useSupportConversationActions,
  useSupportConversations,
  type SupportConversation,
} from '../../hooks/admin/useSupportInbox';
import {
  INK,
  FONTS,
  primaryBtnSx,
  ghostBtnSx,
  ledgerInputSx,
} from '../../styles/ledger';
import { LedgerPage, PageHeader, StatusDot, MonoTag, HeaderIconBtn } from '../../components/admin/LedgerPrimitives';

const waitingTone = (w: SupportConversation['waitingOn']): 'active' | 'inactive' | 'warning' | 'info' | 'neutral' => {
  if (w === 'admin') return 'inactive';
  if (w === 'user') return 'info';
  return 'neutral';
};
const statusTone = (s: SupportConversation['status']): 'active' | 'inactive' | 'warning' | 'info' | 'neutral' =>
  s === 'closed' ? 'neutral' : 'active';

const SupportInboxPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { uuid } = useParams();

  const [search, setSearch] = React.useState('');
  const [waitingOn, setWaitingOn] = React.useState<'all' | 'admin' | 'user'>('admin');
  const [status, setStatus] = React.useState<'all' | 'open' | 'closed'>('open');

  const listQuery = useSupportConversations({
    page: 1,
    per_page: 50,
    search: search.length ? search : undefined,
    waiting_on: waitingOn === 'all' ? undefined : waitingOn,
    status: status === 'all' ? undefined : status,
  });

  const selectedQuery = useSupportConversation(uuid);
  const { reply, close, isReplying, isClosing } = useSupportConversationActions();

  const [replyText, setReplyText] = React.useState('');
  const threadEndRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (threadEndRef.current) threadEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [selectedQuery.data?.data?.messages?.length]);

  const conversations = listQuery.data?.data ?? [];

  const handleSendReply = async () => {
    if (!uuid || !replyText.trim()) return;
    await reply(uuid, replyText.trim());
    setReplyText('');
  };

  const filterChip = (label: string, active: boolean, onClick: () => void) => (
    <Box
      onClick={onClick}
      sx={{
        fontFamily: FONTS.mono,
        fontSize: 10,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        px: 1,
        py: 0.4,
        border: `1px solid ${active ? INK.accent : INK.hairline}`,
        bgcolor: active ? INK.accentSoft : 'transparent',
        color: active ? INK.accent : INK.muted,
        cursor: 'pointer',
        fontWeight: 500,
        '&:hover': { borderColor: INK.accent, color: INK.accent },
      }}
    >
      {label}
    </Box>
  );

  const leftPane = (
    <Box
      sx={{
        border: `1px solid ${INK.hairline}`,
        borderRadius: 0.5,
        bgcolor: INK.surface,
        display: 'flex',
        flexDirection: 'column',
        minHeight: isMobile ? 'auto' : 'calc(100vh - 200px)',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ p: 2.5, borderBottom: `1px solid ${INK.hairline}` }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography sx={{ fontFamily: FONTS.serif, fontSize: 22, color: INK.text, letterSpacing: '-0.01em' }}>
            Inbox
            <Box component="span" sx={{ ml: 1, fontFamily: FONTS.mono, fontSize: 11, color: INK.muted }}>
              {String(conversations.length).padStart(2, '0')}
            </Box>
          </Typography>
          <HeaderIconBtn title="Refresh" onClick={() => listQuery.refetch()}>
            {listQuery.isFetching ? <CircularProgress size={14} sx={{ color: INK.muted }} /> : <RefreshIcon sx={{ fontSize: 16 }} />}
          </HeaderIconBtn>
        </Stack>
        <TextField
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search email, name, ticket…"
          size="small"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16, color: INK.subtle }} />
              </InputAdornment>
            ),
          }}
          sx={ledgerInputSx}
        />
        <Stack direction="row" spacing={0.75} sx={{ mt: 1.5, flexWrap: 'wrap', gap: 0.5 }}>
          {filterChip('Waiting admin', waitingOn === 'admin', () => setWaitingOn('admin'))}
          {filterChip('Waiting user', waitingOn === 'user', () => setWaitingOn('user'))}
          {filterChip('All waiting', waitingOn === 'all', () => setWaitingOn('all'))}
        </Stack>
        <Stack direction="row" spacing={0.75} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
          {filterChip('Open', status === 'open', () => setStatus('open'))}
          {filterChip('Closed', status === 'closed', () => setStatus('closed'))}
          {filterChip('All', status === 'all', () => setStatus('all'))}
        </Stack>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {listQuery.isLoading ? (
          <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={24} sx={{ color: INK.accent }} />
          </Box>
        ) : conversations.length === 0 ? (
          <Box sx={{ p: 3 }}>
            <Typography sx={{ fontFamily: FONTS.serif, fontSize: 20, color: INK.subtle, letterSpacing: '-0.01em' }}>
              No conversations.
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {conversations.map((c) => {
              const isActive = c.uuid === uuid;
              const initials = (c.name || '?').split(' ').slice(0, 2).map((x) => x.charAt(0).toUpperCase()).join('');
              const preview = c.latestMessage?.body?.slice(0, 90) ?? '';
              return (
                <ListItemButton
                  key={c.uuid}
                  selected={isActive}
                  onClick={() => navigate(`/admin/support/${c.uuid}`)}
                  sx={{
                    alignItems: 'flex-start',
                    px: 2.5,
                    py: 1.75,
                    borderBottom: `1px solid ${INK.hairlineSoft}`,
                    borderLeft: isActive ? `2px solid ${INK.accent}` : '2px solid transparent',
                    bgcolor: isActive ? INK.accentSoft : 'transparent',
                    '&:hover': { bgcolor: isActive ? INK.accentSoft : INK.surfaceTint },
                    '&.Mui-selected': { bgcolor: INK.accentSoft, '&:hover': { bgcolor: INK.accentSoft } },
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: 0.5,
                      bgcolor: isActive ? INK.accent : INK.surfaceTint,
                      color: isActive ? INK.surface : INK.text,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontFamily: FONTS.mono,
                      fontSize: 11,
                      fontWeight: 600,
                      letterSpacing: '0.02em',
                      mr: 1.5,
                      flexShrink: 0,
                      border: isActive ? 'none' : `1px solid ${INK.hairline}`,
                    }}
                  >
                    {initials}
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ gap: 1 }}>
                      <Typography sx={{ fontSize: 13.5, color: INK.text, fontWeight: 600, letterSpacing: '-0.005em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {c.subject ?? 'Support'}
                      </Typography>
                      <Typography sx={{ fontFamily: FONTS.mono, fontSize: 10, color: INK.subtle, flexShrink: 0 }}>
                        {c.lastMessageAt ? formatRelativeTime(c.lastMessageAt) : ''}
                      </Typography>
                    </Stack>
                    <Typography sx={{ fontFamily: FONTS.mono, fontSize: 11, color: INK.subtle, mt: 0.25 }}>
                      {c.name} · {c.email}
                    </Typography>
                    {preview && (
                      <Typography sx={{ fontSize: 12, color: INK.muted, mt: 0.5, lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {preview}
                      </Typography>
                    )}
                    <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                      <StatusDot label={c.status === 'closed' ? 'Closed' : 'Open'} tone={statusTone(c.status)} />
                      <StatusDot label={`Wait · ${c.waitingOn || 'idle'}`} tone={waitingTone(c.waitingOn)} />
                    </Stack>
                  </Box>
                </ListItemButton>
              );
            })}
          </List>
        )}
      </Box>
    </Box>
  );

  const rightPane = (
    <Box
      sx={{
        border: `1px solid ${INK.hairline}`,
        borderRadius: 0.5,
        bgcolor: INK.surface,
        display: 'flex',
        flexDirection: 'column',
        minHeight: isMobile ? 'auto' : 'calc(100vh - 200px)',
        overflow: 'hidden',
      }}
    >
      {!uuid ? (
        <Box sx={{ p: 5, textAlign: 'center' }}>
          <Typography sx={{ fontFamily: FONTS.serif, fontSize: 28, color: INK.text, letterSpacing: '-0.01em', mb: 1 }}>
            Pick a conversation.
          </Typography>
          <Typography sx={{ fontFamily: FONTS.sans, fontSize: 13, color: INK.muted }}>
            Select a ticket from the inbox to read and reply.
          </Typography>
        </Box>
      ) : selectedQuery.isLoading ? (
        <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={24} sx={{ color: INK.accent }} />
        </Box>
      ) : selectedQuery.isError ? (
        <Box sx={{ p: 4 }}>
          <Typography sx={{ fontFamily: FONTS.serif, fontSize: 22, color: INK.text, mb: 2 }}>
            Failed to load conversation.
          </Typography>
          <Button onClick={() => selectedQuery.refetch()} sx={ghostBtnSx}>
            Retry
          </Button>
        </Box>
      ) : (
        (() => {
          const c = selectedQuery.data?.data;
          if (!c) return null;
          return (
            <>
              <Box sx={{ p: 3, borderBottom: `1px solid ${INK.hairline}` }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                  <Box>
                    <Typography sx={{ fontFamily: FONTS.serif, fontSize: 24, color: INK.text, letterSpacing: '-0.01em' }}>
                      {c.subject ?? 'Support'}
                    </Typography>
                    <Typography sx={{ fontFamily: FONTS.mono, fontSize: 11.5, color: INK.muted, mt: 0.5 }}>
                      {c.name} · {c.email}{c.phone ? ` · ${c.phone}` : ''}
                    </Typography>
                    <Stack direction="row" spacing={1.5} sx={{ mt: 1.5, flexWrap: 'wrap' }}>
                      <StatusDot label={c.status === 'closed' ? 'Closed' : 'Open'} tone={statusTone(c.status)} />
                      <StatusDot label={`Wait · ${c.waitingOn || 'idle'}`} tone={waitingTone(c.waitingOn)} />
                      {c.messageType && <MonoTag>{c.messageType}</MonoTag>}
                    </Stack>
                  </Box>
                  <Button
                    onClick={async () => {
                      await close(c.uuid);
                      await selectedQuery.refetch();
                      await listQuery.refetch();
                    }}
                    disabled={isClosing || c.status === 'closed'}
                    sx={ghostBtnSx}
                    startIcon={<DoneIcon sx={{ fontSize: 16 }} />}
                  >
                    Resolve
                  </Button>
                </Stack>
              </Box>

              <Box sx={{ flex: 1, p: 3, overflow: 'auto', bgcolor: INK.surfaceTint }}>
                <Stack spacing={1.5}>
                  {(c.messages ?? []).map((m) => {
                    const isAdmin = m.authorType === 'admin';
                    return (
                      <Box key={m.id} sx={{ display: 'flex', justifyContent: isAdmin ? 'flex-end' : 'flex-start' }}>
                        <Box
                          sx={{
                            maxWidth: '78%',
                            p: 1.75,
                            border: `1px solid ${isAdmin ? INK.accent : INK.hairline}`,
                            bgcolor: isAdmin ? INK.accentSoft : INK.surface,
                            borderRadius: 0.5,
                          }}
                        >
                          <Typography sx={{ fontFamily: FONTS.sans, fontSize: 13, whiteSpace: 'pre-wrap', lineHeight: 1.55, color: INK.text }}>
                            {m.body}
                          </Typography>
                          <Typography sx={{ fontFamily: FONTS.mono, fontSize: 10, color: INK.subtle, mt: 0.75, textAlign: isAdmin ? 'right' : 'left', letterSpacing: '0.04em' }}>
                            {isAdmin ? 'Admin' : m.authorName || 'User'} · {formatRelativeTime(m.createdAt)}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                  <div ref={threadEndRef} />
                </Stack>
              </Box>

              <Box sx={{ p: 2.5, borderTop: `1px solid ${INK.hairline}` }}>
                <TextField
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Write a reply…"
                  fullWidth
                  multiline
                  minRows={3}
                  maxRows={8}
                  sx={ledgerInputSx}
                />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
                  <Button
                    sx={primaryBtnSx}
                    startIcon={<ReplyIcon sx={{ fontSize: 16 }} />}
                    onClick={handleSendReply}
                    disabled={isReplying || !replyText.trim() || c.status === 'closed'}
                  >
                    Send reply (email)
                  </Button>
                </Box>
              </Box>
            </>
          );
        })()
      )}
    </Box>
  );

  return (
    <LedgerPage eyebrowFooter="Almaship · Support · Inbox">
      <PageHeader
        eyebrow="Admin · Support"
        title={
          <>
            Support
            <Box component="span" sx={{ fontStyle: 'italic', color: INK.accent }}>
              {' '}inbox.
            </Box>
          </>
        }
        description="Contact-us conversations. Reply by email, or close when resolved."
      />
      {isMobile ? (
        <Box>
          {uuid ? (
            <Box>
              <Button onClick={() => navigate('/admin/support')} sx={{ ...ghostBtnSx, border: 'none', mb: 2 }}>
                ← Back to inbox
              </Button>
              {rightPane}
            </Box>
          ) : (
            leftPane
          )}
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: '380px 1fr', gap: 2 }}>
          {leftPane}
          {rightPane}
        </Box>
      )}
    </LedgerPage>
  );
};

export default SupportInboxPage;
